import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Building2, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CfELogo = "https://customer-assets.emergentagent.com/job_startup-central-3/artifacts/3g256hrq_CfE%20Logo.png";

export default function StartupLoginPage() {
  const navigate = useNavigate();
  const [showPin, setShowPin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    startup_name: '',
    pin: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/startup/login`, formData);
      
      // Store in sessionStorage (clears when browser closes)
      sessionStorage.setItem('startup_token', response.data.access_token);
      sessionStorage.setItem('startup_info', JSON.stringify({
        startup_name: response.data.startup_name,
        founder_name: response.data.founder_name
      }));
      
      toast.success(`Welcome, ${response.data.founder_name}!`, {
        description: 'You can now book advisor sessions.'
      });
      navigate('/advisors/book');
    } catch (error) {
      toast.error('Login failed', {
        description: error.response?.data?.detail || 'Invalid credentials'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 flex items-center justify-center bg-[#F3F4F6] p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <img 
            src={CfELogo} 
            alt="CfE Logo" 
            className="h-20 mx-auto mb-6"
          />
          <h1 className="text-2xl font-bold text-[#231F20] font-['Bricolage_Grotesque']">
            Startup Login
          </h1>
          <p className="text-[#58595B] mt-2">
            Sign in with your registered startup credentials to book an advisor
          </p>
        </div>

        <Card className="shadow-lg">
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label className="form-label">Startup Name</Label>
                <div className="relative">
                  <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="text"
                    value={formData.startup_name}
                    onChange={(e) => setFormData({ ...formData, startup_name: e.target.value })}
                    required
                    className="form-input pl-12"
                    placeholder="Enter your startup name"
                    data-testid="startup-name-input"
                  />
                </div>
              </div>

              <div>
                <Label className="form-label">4-Digit PIN</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type={showPin ? 'text' : 'password'}
                    value={formData.pin}
                    onChange={(e) => setFormData({ ...formData, pin: e.target.value.slice(0, 4) })}
                    required
                    maxLength={4}
                    pattern="[0-9]{4}"
                    className="form-input pl-12 pr-12 tracking-widest text-center text-xl"
                    placeholder="• • • •"
                    data-testid="startup-pin-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPin(!showPin)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPin ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-[#58595B] mt-2">
                  You received this PIN when you registered your startup
                </p>
              </div>

              <Button
                type="submit"
                disabled={loading || formData.pin.length !== 4}
                className="w-full bg-[#075FA3] hover:bg-[#0077B6] text-white rounded-full py-6 font-semibold"
                data-testid="startup-login-btn"
              >
                {loading ? 'Signing in...' : 'Sign In & Book Advisor'}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-gray-100 text-center">
              <p className="text-sm text-[#58595B] mb-3">
                Don't have an account yet?
              </p>
              <Link to="/register">
                <Button variant="outline" className="w-full rounded-full" data-testid="register-link">
                  Register Your Startup
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-[#58595B] mt-6">
          <Link to="/" className="hover:text-[#075FA3]">← Back to website</Link>
        </p>
      </motion.div>
    </div>
  );
}
